import React, { ChangeEvent, ForwardedRef, forwardRef, PropsWithChildren, useCallback, useState } from 'react';
import { CField, CFieldAutoCapitalize, CFieldAutoCorrect, CFieldEnterKeyHint, CFieldInputMode, CFieldProps } from '@/src/components/c-field/CField';
import { CIcon, CIconColor, CIcons } from '@/src/components/c-icon/CIcon';
import classNames from 'classnames';

export interface CFieldPasswordProps extends CFieldProps {
  inputMode?: CFieldInputMode;
  enterKeyHint?: CFieldEnterKeyHint;
}

export const CFieldPassword = forwardRef(function CPasswordField(props: PropsWithChildren<CFieldPasswordProps>, ref: ForwardedRef<HTMLInputElement>): React.JSX.Element {
  const [visible, setVisible] = useState(false);

  const onClick = useCallback(() => {
    return setVisible(prevState => !prevState);
  }, []);

  return (
    <CField
      id={props.id}
      value={props.value}
      label={props.label}
      class={props.class}
      disabled={props.disabled}
      validations={props.validations}
      placeholder={props.placeholder}
      testSelector={props.testSelector}
      required={props.required}
      hideMessageContainer={props.hideMessageContainer}
      fullWidth={props.fullWidth}
      onBlur={props.onBlur}
      onChange={props.onChange}
      onFocus={props.onFocus}
      onKeyUp={props.onKeyUp}
      onKeyDown={props.onKeyDown}
      formId={props.formId}
      security={props.security}
      icon={true}
    >
      {({ id, value, setValue }) => (
        <>
          <input
            ref={ref}
            name={props.name}
            pattern={props.pattern}
            required={props.required}
            disabled={props.disabled}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            aria-disabled={props.disabled}
            className="c-field__input"
            inputMode={props.inputMode}
            enterKeyHint={props.enterKeyHint}
            autoCapitalize={CFieldAutoCapitalize.Off}
            autoCorrect={CFieldAutoCorrect.Off}
            id={id}
            type={visible ? 'text' : 'password'}
            value={value}
            autoComplete={props.autoComplete ?? 'password'}
            placeholder={props.placeholder}
            onChange={(event: ChangeEvent) => setValue(event, (event.currentTarget as HTMLInputElement).value)}
          />
          <CIcon
            class={classNames('c-field__icon', {
              'cursor--disabled': props.disabled,
              'cursor--pointer': !props.disabled,
            })}
            onClick={props.disabled ? undefined : onClick}
            color={props.disabled ? CIconColor.Disabled : CIconColor.Gray1}
            icon={visible ? CIcons.Eye : CIcons.EyeOff}
          />
        </>
      )}
    </CField>
  );
});